import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { 
    ArrowLeft, ArrowRight, Loader2, Check, Sparkles,
    FileSignature, ChevronRight
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUGERENCIAS_DEMOSTRAR = [
    'Que soy capaz', 'Que merezco estar aquí', 'Que soy valioso/a', 
    'Que no soy un fraude', 'Que puedo hacerlo solo/a', 'Que soy profesional'
];

const SUGERENCIAS_MIEDO = [
    'Ser visto como incompetente', 'Decepcionar a otros', 'Perder oportunidades',
    'Que no me tomen en serio', 'Quedar en ridículo', 'No cumplir expectativas'
];

export default function Modulo23ContratoOculto() {
    const [queDemostrar, setQueDemostrar] = useState('');
    const [quedeMiedo, setQuedeMiedo] = useState('');
    const [impactoConductual, setImpactoConductual] = useState('');
    const [costoPatron, setCostoPatron] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');

    useEffect(() => {
        const loadDraft = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/contrato_oculto`, {
                    withCredentials: true
                });
                if (data.data) {
                    if (data.data.queDemostrar) setQueDemostrar(data.data.queDemostrar);
                    if (data.data.quedeMiedo) setQuedeMiedo(data.data.quedeMiedo);
                    if (data.data.impactoConductual) setImpactoConductual(data.data.impactoConductual);
                    if (data.data.costoPatron) setCostoPatron(data.data.costoPatron);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch (error) {
                // silent - draft not found is expected
            } finally {
                setLoadingDraft(false);
            }
        };
        loadDraft();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try {
            await axios.post(
                `${API_URL}/api/user/draft/save`,
                { module_id: 'contrato_oculto', data: { queDemostrar, quedeMiedo, impactoConductual, costoPatron } },
                { withCredentials: true }
            );
        } catch (error) {
            // silent - non-critical draft save
        } finally {
            setSavingDraft(false);
        }
    }, [queDemostrar, quedeMiedo, impactoConductual, costoPatron]);

    useEffect(() => {
        if (loadingDraft || readOnly) return;
        const timer = setTimeout(saveDraft, 2000);
        return () => clearTimeout(timer);
    }, [queDemostrar, quedeMiedo, impactoConductual, costoPatron, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && queDemostrar.trim().length > 0 && 
                      quedeMiedo.trim().length > 0 && 
                      impactoConductual.trim().length > 0 && 
                      costoPatron.trim().length > 0;

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) {
            toast.error('Completa todos los campos');
            return;
        }

        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/user/modules/contrato-oculto/submit`,
                { 
                    que_demostrar: queDemostrar,
                    que_miedo: quedeMiedo,
                    impacto_conductual: impactoConductual,
                    costo_patron: costoPatron
                },
                { withCredentials: true }
            );
            setAiInsight(data.ai_insight);
            setSubmitted(true);
            toast.success('Módulo completado');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al enviar');
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestionClick = (setter, currentValue, suggestion) => {
        if (currentValue.includes(suggestion)) return;
        setter(currentValue ? `${currentValue}, ${suggestion}` : suggestion);
    };

    if (loadingDraft) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" />
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center p-4">
                <Toaster position="top-right" richColors />
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl">
                    <CardContent className="pt-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#5FAF9D] to-[#4A9A89] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                            <FileSignature className="h-10 w-10 text-white" />
                        </div>
                        <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-4">Contrato revelado</h2>
                        
                        <div className="bg-[#F5F7F8] rounded-xl p-4 text-left mb-6">
                            <p className="text-xs text-[#7A8A93] uppercase mb-2">Tu contrato oculto</p>
                            <p className="text-[#1F2A30]">
                                "Necesito demostrar <span className="font-medium text-[#3E5F6E]">{queDemostrar}</span> porque 
                                temo <span className="font-medium text-[#D07F7E]">{quedeMiedo}</span>"
                            </p>
                        </div>
                        
                        {aiInsight && (
                            <div className="bg-[#F5F7F8] border-l-4 border-[#5FAF9D] p-4 rounded-r-xl text-left mb-6">
                                <div className="flex items-start gap-3">
                                    <Sparkles className="h-5 w-5 text-[#5FAF9D] shrink-0 mt-0.5" />
                                    <p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p>
                                </div>
                            </div>
                        )}
                        
                        <div className="mb-6"><ModuleFeedback moduleId="contrato_oculto" chapter={2} /></div>
                        <Link to="/usuario">
                            <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl">
                                Continuar
                                <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Link to="/usuario">
                                <Button variant="ghost" size="icon" className="text-[#3E5F6E]">
                                    <ArrowLeft className="h-5 w-5" />
                                </Button>
                            </Link>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs font-semibold text-[#5FAF9D] uppercase tracking-wide">Capítulo 2</span>
                                    <span className="text-xs text-[#7A8A93]">•</span>
                                    <span className="text-xs text-[#7A8A93]">Módulo 2.3</span>
                                </div>
                                <h1 className="text-lg font-semibold text-[#1F2A30] font-['Outfit']">Contrato oculto de rendimiento</h1>
                            </div>
                        </div>
                        {savingDraft && !readOnly && (
                            <span className="text-xs text-[#7A8A93] flex items-center gap-1">
                                <Loader2 className="h-3 w-3 animate-spin" />
                            </span>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E]/5 to-transparent p-8">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-xl flex items-center justify-center shadow-md">
                                <FileSignature className="h-6 w-6 text-white" />
                            </div>
                            <div>
                                <CardTitle className="text-[#1F2A30]">¿Qué necesitas demostrar?</CardTitle>
                                <p className="text-sm text-[#4A5B63]">Descubre el contrato no escrito que llevas contigo</p>
                            </div>
                        </div>
                    </CardHeader>
                    
                    <CardContent className="p-8 space-y-8">
                        {/* Que demostrar */}
                        <div className="space-y-3">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Qué sientes que necesitas demostrar con esta tarea?
                            </Label>
                            <Textarea
                                value={queDemostrar}
                                onChange={(e) => setQueDemostrar(e.target.value)}
                                placeholder="Ej: Que soy capaz, que merezco este puesto, que puedo con todo..."
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                data-testid="que-demostrar-input"
                            />
                            <div className="flex flex-wrap gap-2">
                                {SUGERENCIAS_DEMOSTRAR.map((sug) => (
                                    <button
                                        key={sug}
                                        onClick={() => handleSuggestionClick(setQueDemostrar, queDemostrar, sug)}
                                        className="text-xs px-3 py-1.5 bg-[#F5F7F8] hover:bg-[#3E5F6E]/10 text-[#4A5B63] rounded-full border border-[#DCE3E8] transition-colors"
                                    >
                                        {sug}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Que miedo */}
                        <div className="space-y-3">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Qué temes que pase si no lo demuestras?
                            </Label>
                            <Textarea
                                value={quedeMiedo}
                                onChange={(e) => setQuedeMiedo(e.target.value)}
                                placeholder="Ej: Que piensen que soy incompetente, que me rechacen..."
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[80px]"
                                data-testid="que-miedo-input"
                            />
                            <div className="flex flex-wrap gap-2">
                                {SUGERENCIAS_MIEDO.map((sug) => (
                                    <button
                                        key={sug}
                                        onClick={() => handleSuggestionClick(setQuedeMiedo, quedeMiedo, sug)}
                                        className="text-xs px-3 py-1.5 bg-[#F5F7F8] hover:bg-[#D07F7E]/10 text-[#4A5B63] rounded-full border border-[#DCE3E8] transition-colors"
                                    >
                                        {sug}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Impacto conductual */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Cómo impacta este contrato en tu conducta?
                            </Label>
                            <Textarea
                                value={impactoConductual}
                                onChange={(e) => setImpactoConductual(e.target.value)}
                                placeholder="¿Qué haces o dejas de hacer por este contrato oculto?"
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[100px]"
                                data-testid="impacto-input"
                            />
                        </div>

                        {/* Costo del patrón */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">
                                ¿Cuál es el costo emocional de mantener este patrón?
                            </Label>
                            <Textarea
                                value={costoPatron}
                                onChange={(e) => setCostoPatron(e.target.value)}
                                placeholder="¿Qué te cuesta emocionalmente vivir bajo esta presión?"
                                className="border-[#DCE3E8] bg-[#F5F7F8] focus:ring-[#5FAF9D]/30 focus:border-[#5FAF9D] rounded-xl min-h-[100px]"
                                data-testid="costo-input"
                            />
                        </div>

                        <div className="flex justify-end pt-6 border-t border-[#DCE3E8]">
                            <Button
                                onClick={handleSubmit}
                                disabled={!canSubmit || loading}
                                className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                                data-testid="submit-contrato"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    <>
                                        Completar módulo
                                        <Check className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
